/**
 * Validación con punto en movimiento (seguimiento suave).
 *
 * El objetivo recorre una curva de Lissajous con frecuencias distintas en x e y, de modo que la
 * correlación cruzada entre mirada y objetivo tiene un único máximo claro. El retraso estimado es
 * el desplazamiento temporal que maximiza la correlación media de x e y; el error espacial se mide
 * con ese desplazamiento.
 */

export const PURSUIT_DURATION_MS = 12_000;
/** Tiempo inicial con el punto quieto para que la mirada lo encuentre (no se analiza). */
export const PURSUIT_LEAD_IN_MS = 1_500;
export const PURSUIT_MAX_LAG_MS = 1_000;
export const PURSUIT_LAG_STEP_MS = 10;

const FX = 0.15;
const FY = 0.23;

/** Posición del objetivo (px CSS) en el instante `tMs` del movimiento (antes de 0 queda quieto). */
export function pursuitTarget(tMs: number, viewportWidth: number, viewportHeight: number): { x: number; y: number } {
  const t = Math.max(0, Math.min(PURSUIT_DURATION_MS, tMs)) / 1000;
  return {
    x: viewportWidth / 2 + viewportWidth * 0.35 * Math.sin(2 * Math.PI * FX * t),
    y: viewportHeight / 2 + viewportHeight * 0.3 * Math.sin(2 * Math.PI * FY * t + Math.PI / 2),
  };
}

function pearson(a: ReadonlyArray<number>, b: ReadonlyArray<number>): number | null {
  const n = a.length;
  if (n < 3) return null;
  let ma = 0;
  let mb = 0;
  for (let i = 0; i < n; i++) {
    ma += a[i]!;
    mb += b[i]!;
  }
  ma /= n;
  mb /= n;
  let sab = 0;
  let saa = 0;
  let sbb = 0;
  for (let i = 0; i < n; i++) {
    const da = a[i]! - ma;
    const db = b[i]! - mb;
    sab += da * db;
    saa += da * da;
    sbb += db * db;
  }
  if (saa === 0 || sbb === 0) return null;
  return sab / Math.sqrt(saa * sbb);
}

export interface PursuitEstimate {
  lagMs: number | null;
  correlation: number | null;
  meanErrorPx: number | null;
  /** Muestras usadas con el retraso estimado. */
  samplesUsed: number;
}

/**
 * Estima retraso y error a partir de muestras `(tMs, x, y)` con `tMs` relativo al inicio del
 * movimiento. Solo se usan muestras cuyo instante corregido cae dentro del movimiento.
 */
export function estimatePursuit(
  samples: ReadonlyArray<{ tMs: number; x: number; y: number }>,
  viewportWidth: number,
  viewportHeight: number,
  opts: { maxLagMs?: number; stepMs?: number; minSamples?: number } = {},
): PursuitEstimate {
  const maxLag = opts.maxLagMs ?? PURSUIT_MAX_LAG_MS;
  const step = opts.stepMs ?? PURSUIT_LAG_STEP_MS;
  const minSamples = opts.minSamples ?? 30;
  let best: { lag: number; r: number } | null = null;
  for (let lag = 0; lag <= maxLag; lag += step) {
    const gx: number[] = [];
    const gy: number[] = [];
    const tx: number[] = [];
    const ty: number[] = [];
    for (const s of samples) {
      const tc = s.tMs - lag;
      if (tc < 0 || tc > PURSUIT_DURATION_MS) continue;
      const p = pursuitTarget(tc, viewportWidth, viewportHeight);
      gx.push(s.x);
      gy.push(s.y);
      tx.push(p.x);
      ty.push(p.y);
    }
    if (gx.length < minSamples) continue;
    const rx = pearson(gx, tx);
    const ry = pearson(gy, ty);
    if (rx === null || ry === null) continue;
    const r = (rx + ry) / 2;
    if (!best || r > best.r) best = { lag, r };
  }
  if (!best) return { lagMs: null, correlation: null, meanErrorPx: null, samplesUsed: 0 };
  let err = 0;
  let used = 0;
  for (const s of samples) {
    const tc = s.tMs - best.lag;
    if (tc < 0 || tc > PURSUIT_DURATION_MS) continue;
    const p = pursuitTarget(tc, viewportWidth, viewportHeight);
    err += Math.hypot(s.x - p.x, s.y - p.y);
    used++;
  }
  return { lagMs: best.lag, correlation: best.r, meanErrorPx: used ? err / used : null, samplesUsed: used };
}
